import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import BookingFilters, { BookingFilters as BookingFiltersType, BookingFilterOptions } from "@/components/ui/BookingFilters";
import { 
  User, 
  MapPin, 
  CreditCard, 
  Bell, 
  Gift, 
  Settings, 
  Trash2, 
  Plus, 
  Edit,
  Star,
  Shield,
  Clock,
  Calendar,
  Mail,
  Phone,
  Globe,
  Lock,
  Eye,
  EyeOff,
  MessageSquare,
  FileText
} from "lucide-react";
import ProfileBillingSection from "@/components/profile/ProfileBillingSection";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { saveAs } from "file-saver";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { Seo } from "@/components/seo/Seo";
import { formatCurrency } from "@/lib/currency";
import { LEGAL_VERSIONS } from "@/lib/legal";
import { useMfaStepUp } from "@/hooks/useMfaStepUp";

type TFn = (key: string, fallback?: string) => string;

const createProfileSchema = (t: TFn) =>
  z.object({
    firstName: z.string().min(1, t("profile.validation.first_name")),
    lastName: z.string().min(1, t("profile.validation.last_name")),
    nickname: z.string().optional(),
    email: z.string().email(t("profile.validation.email")),
    phone: z.string().min(10, t("profile.validation.phone")),
    bio: z.string().optional(),
    website: z.string().url().optional().or(z.literal("")),
  });

const createAddressSchema = (t: TFn) =>
  z.object({
    label: z.string().min(1, t("profile.validation.label")),
    addressLine1: z.string().min(1, t("profile.validation.address")),
    addressLine2: z.string().optional(),
    city: z.string().min(1, t("profile.validation.city")),
    state: z.string().min(1, t("profile.validation.state")),
    postalCode: z.string().min(1, t("profile.validation.postal")),
    country: z.string().min(1, t("profile.validation.country")),
    isDefault: z.boolean().default(false),
  });

const createPaymentSchema = (t: TFn) =>
  z.object({
    cardNumber: z.string().min(16, t("profile.validation.card_number")),
    cardholderName: z.string().min(1, t("profile.validation.cardholder")),
    expiryMonth: z.string().min(1, t("profile.validation.expiry_month")),
    expiryYear: z.string().min(1, t("profile.validation.expiry_year")),
    cvv: z.string().min(3, t("profile.validation.cvv")),
    isDefault: z.boolean().default(false),
  });

type ProfileFormData = z.infer<ReturnType<typeof createProfileSchema>>;
type AddressFormData = z.infer<ReturnType<typeof createAddressSchema>>;
type PaymentFormData = z.infer<ReturnType<typeof createPaymentSchema>>;

type Address = {
  id: string;
  label: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
};

type PaymentMethod = {
  id: string;
  type: string;
  last4: string;
  cardholderName: string;
  expires: string;
  isDefault: boolean;
};

// Mock user data
const mockUser = {
  firstName: "John",
  lastName: "Doe",
  nickname: "Johnny",
  email: "john.doe@email.com",
  phone: "+1 (555) 123-4567",
  bio: "Business owner who values quality cleaning services for my office and home.",
  website: "https://johndoe.com",
  avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
  addresses: [
    {
      id: "1",
      label: "Home",
      addressLine1: "123 Main Street",
      addressLine2: "Apt 4B",
      city: "Downtown",
      state: "NY",
      postalCode: "12345",
      country: "USA",
      isDefault: true,
    },
    {
      id: "2",
      label: "Office",
      addressLine1: "456 Business Ave",
      addressLine2: "Suite 200",
      city: "Corporate District",
      state: "NY",
      postalCode: "12345",
      country: "USA",
      isDefault: false,
    }
  ] as Address[],
  paymentMethods: [
    {
      id: "1",
      type: "Visa",
      last4: "1234",
      cardholderName: "John Doe",
      expires: "12/25",
      isDefault: true,
    },
    {
      id: "2",
      type: "Mastercard",
      last4: "5678",
      cardholderName: "John Doe",
      expires: "08/26",
      isDefault: false,
    }
  ] as PaymentMethod[],
  loyaltyPoints: 1250,
  totalSpent: 2850,
  totalOrders: 12,
  memberSince: "2023-01-15",
  notifications: {
    email: {
      bookingConfirmations: true,
      bookingReminders: true,
      promotionalOffers: false,
      newsletter: true,
    },
    sms: {
      bookingConfirmations: true,
      bookingReminders: true,
      promotionalOffers: false,
    },
    push: {
      bookingUpdates: true,
      newServices: true,
      specialOffers: true,
    }
  }
};

// Feature flag: hide customer-side quote requests UI until the flow is
// finalized. Flip to true to bring the tab back.
const QUOTES_ENABLED = false;

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, lang, tx } = useI18n();
  const { toast } = useToast();
  const { ensureAAL2, StepUpDialog } = useMfaStepUp();
  const [activeTab, setActiveTab] = useState("bookings");
  const [notifications, setNotifications] = useState(mockUser.notifications);
  const [showPassword, setShowPassword] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [resModal, setResModal] = useState<{ id: string | null, date: string, time: string, reason: string }>({ id: null, date: "", time: "", reason: "" });
  const [cancelModal, setCancelModal] = useState<{ id: string | null, reason: string }>({ id: null, reason: "" });
  const [cancelAck, setCancelAck] = useState<boolean>(false);
  const [cancelInfo, setCancelInfo] = useState<{
    booking_number?: string;
    total_paid?: number;
    platform_fee?: number;
    vendor_fee?: number;
    estimated_refund?: number;
    within48?: boolean;
    retained_total?: number;
    stripe_fee?: number;
    vendor_email?: string | null;
    vendor_phone?: string | null;
    company_name?: string | null;
  } | null>(null);
  const [acceptForQuoteId, setAcceptForQuoteId] = useState<string | null>(null);
  const [acceptDate, setAcceptDate] = useState<string>("");
  const [acceptTime, setAcceptTime] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(mockUser.paymentMethods);
  const [favourites, setFavourites] = useState<any[]>([]);
  const [overview, setOverview] = useState<{ totalSpent: number; totalOrders: number; upcomingCount: number; favouritesCount: number }>({ totalSpent: 0, totalOrders: 0, upcomingCount: 0, favouritesCount: 0 });
  const [bookings, setBookings] = useState<any[]>([]);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [avatarInitials, setAvatarInitials] = useState<{ first: string; last: string }>({ first: '', last: '' });
  const [reviewsByBookingId, setReviewsByBookingId] = useState<Record<string, any>>({});
  const [reviewModal, setReviewModal] = useState<{ open: boolean; booking: any | null; rating: number; comment: string; loading: boolean }>({
    open: false,
    booking: null,
    rating: 5,
    comment: "",
    loading: false,
  });
  const [loadingBookings, setLoadingBookings] = useState<boolean>(false);
  const [customerConsent, setCustomerConsent] = useState<boolean>(true);
  const [marketingConsent, setMarketingConsent] = useState<boolean>(false);
  const [consentInfo, setConsentInfo] = useState<{terms_version?: string|null; privacy_version?: string|null; consented_at?: string|null}>({});
  const [bookingFilters, setBookingFilters] = useState<BookingFiltersType>({
    search: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const [bookingFilterOptions, setBookingFilterOptions] = useState<BookingFilterOptions>({
    companies: [],
    services: []
  });

  // MFA (TOTP) - Supabase Auth
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string>("");
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [mfaEnrolled, setMfaEnrolled] = useState<any>(null);
  const [mfaChallenge, setMfaChallenge] = useState<any>(null);
  const [mfaCode, setMfaCode] = useState<string>("");
  const [mfaQrSvg, setMfaQrSvg] = useState<string>("");
  const [mfaUri, setMfaUri] = useState<string>("");

  const normalizeProfileTab = (tab: string) => {
    if (tab === 'payments') return 'billing';
    // Quotes hidden behind QUOTES_ENABLED — if a deep link points at it,
    // gracefully fall back to bookings instead of rendering an empty panel.
    if (tab === 'quotes' && !QUOTES_ENABLED) return 'bookings';
    const allowed = ['bookings','personal','addresses','notifications','loyalty','security','billing'];
    if (QUOTES_ENABLED) allowed.push('quotes');
    return allowed.includes(tab) ? tab : 'bookings';
  };

  const isVerifiedTotp = (f: any) => {
    if (!f) return false;
    const type = String(f?.factor_type ?? f?.type ?? "").toLowerCase();
    const status = String(f?.status ?? f?.factor_status ?? "").toLowerCase();
    return type === "totp" && (status === "verified" || f?.verified === true);
  };

  const loadMfa = async () => {
    try {
      setMfaError("");
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) return;
      const { data, error } = await mfa.listFactors();
      if (error) throw error;
      const factors = data?.totp || data?.all || [];
      setMfaFactors(Array.isArray(factors) ? factors : []);
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.load_failed"));
    }
  };

  const startEnable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    setMfaCode("");
    setMfaEnrolled(null);
    setMfaChallenge(null);
    setMfaQrSvg("");
    setMfaUri("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");

      // Cleanup any leftover unverified factors (common if user started setup and closed modal)
      try {
        const { data: listData } = await mfa.listFactors();
        const all = (listData?.totp || listData?.all || []) as any[];
        const leftovers = (all || []).filter((f) => String(f?.factor_type ?? f?.type ?? "").toLowerCase() === "totp" && !isVerifiedTotp(f));
        for (const f of leftovers) {
          if (f?.id) {
            await mfa.unenroll({ factorId: f.id }).catch(() => {});
          }
        }
      } catch {}

      // Use a non-empty unique friendly name to avoid "already exists"
      const friendlyName = `Halomora TOTP (${new Date().toISOString()})`;
      const { data: enrollData, error: enrollError } = await mfa.enroll({ factorType: "totp", friendlyName });
      if (enrollError) throw enrollError;
      setMfaEnrolled(enrollData);
      setMfaQrSvg(enrollData?.totp?.qr_code || "");
      setMfaUri(enrollData?.totp?.uri || "");
      const factorId = enrollData?.id;
      if (!factorId) throw new Error("Missing factor id");
      const { data: ch, error: chErr } = await mfa.challenge({ factorId });
      if (chErr) throw chErr;
      setMfaChallenge(ch);
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.setup_failed"));
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyEnable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");
      const factorId = mfaEnrolled?.id;
      const challengeId = mfaChallenge?.id;
      if (!factorId || !challengeId) throw new Error("Missing challenge");
      const code = (mfaCode || "").replace(/\s/g, "");
      const { error } = await mfa.verify({ factorId, challengeId, code });
      if (error) throw error;
      await loadMfa();
      toast({ title: t("profile.toast.mfa_enabled"), description: t("profile.toast.mfa_enabled_desc") });
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.invalid_code"));
    } finally {
      setMfaLoading(false);
    }
  };

  const disable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");
      const verified = (mfaFactors || []).find((f: any) => isVerifiedTotp(f));
      const factorId = verified?.id || (mfaFactors || [])?.[0]?.id;
      if (!factorId) throw new Error("No factor to remove");
      const { error } = await mfa.unenroll({ factorId });
      if (error) throw error;
      await loadMfa();
      toast({ title: t("profile.toast.mfa_disabled_title") });
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.disable_failed"));
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    loadMfa();
  }, []);

  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Apply search filter
    if (bookingFilters.search) {
      const searchLower = bookingFilters.search.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.booking_number?.toLowerCase().includes(searchLower) ||
        booking.companies?.company_name?.toLowerCase().includes(searchLower) ||
        booking.services?.name?.toLowerCase().includes(searchLower) ||
        booking.status?.toLowerCase().includes(searchLower)
      );
    }

    // Apply company filter (by real company_id)
    if (bookingFilters.companyId) {
      filtered = filtered.filter(booking => booking.company_id === bookingFilters.companyId);
    }

    // Apply service filter (by real service_id)
    if (bookingFilters.serviceId) {
      filtered = filtered.filter(booking => booking.service_id === bookingFilters.serviceId);
    }

    // Apply status filter
    if (bookingFilters.status) {
      filtered = filtered.filter(booking => booking.status === bookingFilters.status);
    }

    // Apply date filter
    if (bookingFilters.dateFrom || bookingFilters.dateTo) {
      // Compare as yyyy-MM-dd strings to avoid timezone drift.
      const from = bookingFilters.dateFrom ? bookingFilters.dateFrom.toISOString().slice(0, 10) : null;
      const to = bookingFilters.dateTo ? bookingFilters.dateTo.toISOString().slice(0, 10) : null;
      filtered = filtered.filter(booking => {
        const d = String(booking.scheduled_date || '').slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (bookingFilters.sortBy) {
        case 'date':
          const dateA = new Date(`${a.scheduled_date}T${a.scheduled_time || '00:00'}`);
          const dateB = new Date(`${b.scheduled_date}T${b.scheduled_time || '00:00'}`);
          comparison = dateA.getTime() - dateB.getTime();
          break;
        case 'amount':
          comparison = (a.total_amount || 0) - (b.total_amount || 0);
          break;
        case 'created':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
      }

      // Stable tiebreaker so equal keys don't reorder between renders.
      if (comparison === 0) {
        comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
        if (comparison === 0) comparison = String(a.id).localeCompare(String(b.id));
      }
      return bookingFilters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [bookings, bookingFilters]);

  const profileSchema = useMemo(() => createProfileSchema(t), [t]);
  const addressSchema = useMemo(() => createAddressSchema(t), [t]);
  const paymentSchema = useMemo(() => createPaymentSchema(t), [t]);

  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const editAddressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      label: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      isDefault: false,
    },
  });

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: mockUser.firstName,
      lastName: mockUser.lastName,
      nickname: mockUser.nickname,
      email: mockUser.email,
      phone: mockUser.phone,
      bio: mockUser.bio,
      website: mockUser.website,
    },
  });

  const addressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      label: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      isDefault: false,
    },
  });

  const paymentForm = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      cardNumber: "",
      cardholderName: "",
      expiryMonth: "",
      expiryYear: "",
      cvv: "",
      isDefault: false,
    },
  });

  const onSubmitProfile = async (data: ProfileFormData) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session;
      if (!session?.user) {
        toast({ title: t("profile.toast.sign_in_required"), description: t("profile.toast.sign_in_update"), variant: "destructive" });
        navigate("/auth");
        return;
      }

      const userId = session.user.id;
      const prof = await supabase.from("profiles").select("id").eq("user_id", userId).maybeSingle();
      const profId = prof.data?.id;
      if (!profId) {
        toast({ title: t("profile.toast.profile_missing"), description: t("profile.toast.profile_missing_desc"), variant: "destructive" });
        return;
      }

      const fullName = `${String(data.firstName || "").trim()} ${String(data.lastName || "").trim()}`.trim();
      const payload: any = {
        full_name: fullName ? fullName : null,
        phone: String(data.phone || "").trim() || null,
      };

      const { error } = await (supabase as any).from("profiles").update(payload).eq("id", profId);
      if (error) throw error;

      toast({
        title: t("profile.toast.profile_updated"),
        description: t("profile.toast.profile_updated_desc"),
      });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e?.message || t("profile.toast.update_failed"), variant: "destructive" });
    }
  };

  const onSubmitAddress = async (data: AddressFormData) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session;
      if (!session) {
        toast({ title: t("profile.toast.sign_in_required"), description: t("profile.toast.sign_in_addresses"), variant: "destructive" });
        return;
      }
      const { data: prof } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", session.user.id)
        .maybeSingle();
      const profileId = prof?.id;
      if (!profileId) {
        toast({ title: t("profile.toast.profile_missing"), description: t("profile.toast.profile_missing_desc"), variant: "destructive" });
        return;
      }
      const { error } = await supabase.from("service_addresses").insert({
        profile_id: profileId,
        label: data.label,
        address_line1: data.addressLine1,
        address_line2: data.addressLine2 || null,
        city: data.city,
        postal_code: data.postalCode,
        country: data.country,
        is_default: data.isDefault || false,
      } as any);
      if (error) throw error;
      // Reload list
      await loadAddresses();
      addressForm.reset();
      toast({ title: t("profile.toast.address_added"), description: t("profile.toast.address_added_desc") });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.add_address_failed"), variant: "destructive" });
    }
  };

  const onSubmitPayment = (data: PaymentFormData) => {
    const newPayment: PaymentMethod = { 
      id: Date.now().toString(),
      type: "Visa", // This would be determined by card number
      last4: data.cardNumber.slice(-4),
      cardholderName: data.cardholderName,
      expires: `${data.expiryMonth}/${data.expiryYear}`,
      isDefault: data.isDefault || false
    };
    setPaymentMethods(prev => [...prev, newPayment]);
    paymentForm.reset();
    toast({
      title: t("profile.toast.payment_added"),
      description: t("profile.toast.payment_added_desc"),
    });
  };

  const handleNotificationChange = (category: string, type: string, enabled: boolean) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [type]: enabled
      }
    }));
    const friendlyType = type.replace(/([A-Z])/g, " $1").toLowerCase().trim();
    toast({
      title: t("profile.toast.notifications_updated"),
      description: enabled
        ? tx("profile.toast.notification_type_enabled", { type: friendlyType })
        : tx("profile.toast.notification_type_disabled", { type: friendlyType }),
    });
  };

  const setDefaultAddress = async (id: string) => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session;
      if (!session) { toast({ title: t("profile.toast.sign_in_required"), variant: "destructive" }); return; }
      const { data: prof } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', session.user.id)
        .maybeSingle();
      const profileId = prof?.id;
      if (!profileId) return;
      // Unset all, then set selected
      await (supabase as any)
        .from('service_addresses')
        .update({ is_default: false })
        .eq('profile_id', profileId);
      await (supabase as any)
        .from('service_addresses')
        .update({ is_default: true })
        .eq('id', id);
      setAddresses(prev => prev.map(a => ({ ...a, isDefault: a.id === id })));
      toast({ title: t('profile.toast.default_updated'), description: t('profile.toast.default_updated_desc') });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.set_default_failed"), variant: "destructive" });
    }
  };

  const updateAddress = async (id: string, data: AddressFormData) => {
    try {
      const { error } = await (supabase as any)
        .from('service_addresses')
        .update({
          label: data.label,
          address_line1: data.addressLine1,
          address_line2: data.addressLine2 || null,
          city: data.city,
          postal_code: data.postalCode,
          country: data.country,
          is_default: data.isDefault || false,
        })
        .eq('id', id);
      if (error) throw error;
      // Refresh list and close dialog
      await loadAddresses();
      setEditingAddress(null);
      toast({ title: t("profile.toast.address_updated"), description: t("profile.toast.address_updated_desc") });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.update_address_failed"), variant: "destructive" });
    }
  };

  const removeAddress = async (id: string) => {
    try {
      // Soft delete: set is_active=false
      const { error } = await (supabase as any)
        .from("service_addresses")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
      setAddresses(prev => prev.filter(addr => addr.id !== id));
      toast({ title: t("profile.toast.address_removed"), description: t("profile.toast.address_removed_desc") });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.remove_address_failed"), variant: "destructive" });
    }
  };

  const removePaymentMethod = (id: string) => {
    setPaymentMethods(prev => prev.filter(payment => payment.id !== id));
    toast({
      title: t("profile.toast.payment_removed"),
      description: t("profile.toast.payment_removed_desc"),
    });
  };

  const handleExportData = async () => {
    try {
      const ok = await ensureAAL2();
      if (!ok) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const { data, error } = await (supabase as any).rpc('export_my_data');
      if (error) throw error;
      const blob = new Blob([JSON.stringify(data || {}, null, 2)], { type: 'application/json' });
      saveAs(blob, 'halomora-data-export.json');
      toast({ title: t('profile.toast.export_ready'), description: t('profile.toast.export_ready_desc') });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.export_failed"), variant: "destructive" });
    }
  };

  const handleRequestDeletion = async () => {
    try {
      const ok = await ensureAAL2();
      if (!ok) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const { error } = await (supabase as any).rpc('request_account_deletion');
      if (error) throw error;
      toast({ title: t('profile.toast.account_deletion'), description: t('profile.toast.account_deletion_desc') });
      try { await supabase.auth.signOut(); } catch {}
      navigate('/');
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.delete_failed"), variant: "destructive" });
    }
  };

  const loadAddresses = async () => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session;
      if (!session) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", session.user.id)
        .maybeSingle();
      const profileId = prof?.id;
      if (!profileId) return;
      const { data: rows } = await (supabase as any)
        .from("service_addresses")
        .select("id,label,address_line1,address_line2,city,postal_code,country,is_default,is_active")
        .eq("profile_id", profileId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      const mapped: Address[] = (rows || []).map(r => ({
        id: r.id,
        label: r.label,
        addressLine1: r.address_line1,
        addressLine2: r.address_line2 || "",
        city: r.city,
        state: "",
        postalCode: r.postal_code,
        country: r.country,
        isDefault: !!r.is_default,
      }));
      setAddresses(mapped);
    } catch {}
  };

  // Load bookings, consent, addresses, and quotes
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { navigate('/auth'); return; }
        const userId = session.user.id;
        const prof = await supabase.from('profiles').select('id').eq('user_id', userId).maybeSingle();
        if (prof.error || !prof.data) return;
        setProfileId(prof.data.id);
        // Load basic profile fields for the Personal Information form
        try {
          const { data: pRow } = await (supabase as any)
            .from("profiles")
            .select("full_name,email,phone")
            .eq("id", prof.data.id)
            .maybeSingle();

          const full = String(pRow?.full_name || "").trim();
          const parts = full ? full.split(/\s+/g) : [];
          const firstName = parts.length ? parts[0] : profileForm.getValues("firstName");
          const lastName = parts.length >= 2 ? parts.slice(1).join(" ") : profileForm.getValues("lastName");

          // Update avatar initials with real user data
          setAvatarInitials({
            first: String(firstName || "").trim().charAt(0).toUpperCase(),
            last: String(lastName || "").trim().charAt(0).toUpperCase(),
          });

          profileForm.reset({
            firstName: String(firstName || "").trim() || "",
            lastName: String(lastName || "").trim() || "",
            nickname: "",
            email: String(pRow?.email || session.user.email || "").trim() || "",
            phone: String(pRow?.phone || "").trim() || "",
            bio: "",
            website: "",
          });
        } catch {
          // non-blocking
        }
        // Load consent if column exists
        try {
          const { data: consentRow } = await (supabase as any).from('profiles').select('customer_consent').eq('id', prof.data.id).maybeSingle();
          if (consentRow && typeof consentRow.customer_consent === 'boolean') setCustomerConsent(!!consentRow.customer_consent);
          else setCustomerConsent(true);
        } catch { setCustomerConsent(true); }
        // Load marketing consent
        try {
          const { data: mc } = await (supabase as any)
            .from('profiles')
            .select('marketing_consent, terms_version, privacy_version, consented_at')
            .eq('id', prof.data.id)
            .maybeSingle();
          if (mc && typeof mc.marketing_consent === 'boolean') setMarketingConsent(!!mc.marketing_consent);
          if (mc) setConsentInfo({ terms_version: mc.terms_version, privacy_version: mc.privacy_version, consented_at: mc.consented_at });
        } catch {}
        setLoadingBookings(true);
        const res = await supabase
          .from('bookings')
          .select(`id, company_id, service_id, booking_number, status, created_at, scheduled_date, scheduled_time, total_amount, customer_fee_cents, pricing_snapshot, series_id, is_recurring_parent, payment_method, payment_intent_id, charge_id, receipt_url, batch_id, companies ( company_name, email, phone ), services ( name )`)
          .eq('customer_id', prof.data.id)
          .order('scheduled_date', { ascending: false })
          .order('scheduled_time', { ascending: false });
        if (!res.error) {
          const bookingsData = res.data || [];
          // Attach receipt_url from payments (latest per booking)
          try {
            const ids = bookingsData.map((b: any) => b.id);
            if (ids.length > 0) {
              const pay = await (supabase as any)
                .from('payments')
                .select('booking_id, receipt_url')
                .in('booking_id', ids);
              const byId: Record<string, string | null> = {};
              (pay.data || []).forEach((p: any) => { if (p.receipt_url && !byId[p.booking_id]) byId[p.booking_id] = p.receipt_url; });
              bookingsData.forEach((b: any) => { (b as any).receipt_url = byId[b.id] || (b as any).receipt_url || null; });
            }
          } catch {}
          setBookings(bookingsData);

          // Load reviews for these bookings (so we can show "Leave a review" only when needed)
          try {
            const ids = bookingsData.map((b: any) => b.id).filter(Boolean);
            if (ids.length > 0) {
              const rv = await (supabase as any)
                .from('reviews')
                .select('id, booking_id, rating, comment, created_at')
                .in('booking_id', ids);
              const map: Record<string, any> = {};
              (rv.data || []).forEach((r: any) => { if (r?.booking_id) map[r.booking_id] = r; });
              setReviewsByBookingId(map);
              bookingsData.forEach((b: any) => { (b as any).review = map[b.id] || null; });
            }
          } catch {}
          
          // Load filter options
          const companyMap = new Map<string, string>();
          bookingsData.forEach((b: any) => {
            if (b.company_id && b.companies?.company_name) companyMap.set(b.company_id, b.companies.company_name);
          });
          const companies = Array.from(companyMap, ([id, name]) => ({
            id,
            name,
            slug: name.toLowerCase().replace(/\s+/g, '-')
          }));

          const serviceMap = new Map<string, string>();
          bookingsData.forEach((b: any) => {
            if (b.service_id && b.services?.name) serviceMap.set(b.service_id, b.services.name);
          });
          const services = Array.from(serviceMap, ([id, name]) => ({ id, name }));

          setBookingFilterOptions({ companies, services });
        }

        // Load quote requests
        const qr = await (supabase as any)
          .from('quote_requests')
          .select('id, company_id, service_id, status, quote_price, quote_message, created_at')
          .eq('customer_id', prof.data.id)
          .order('created_at', { ascending: false });
        if (!qr.error) setQuotes(qr.data || []);

        const fav = await (supabase as any)
          .from('favourites')
          .select('id, company_id, companies!inner(id, company_name, logo_url, slug)')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        if (!fav.error) setFavourites(fav.data || []);

        // Compute overview metrics
        const bks = res.data || [];
        const totalSpent = (bks.filter((b: any) => String(b.status) === 'completed').reduce((s: number, b: any) => s + Number(b.total_amount || 0), 0));
        const totalOrders = bks.length;
        const today = new Date().toISOString().slice(0,10);
        const upcomingCount = bks.filter((b: any) => ['pending','confirmed','in_progress'].includes(String(b.status)) && String(b.scheduled_date || '') >= today).length;
        const favouritesCount = (fav.data || []).length;
        setOverview({ totalSpent, totalOrders, upcomingCount, favouritesCount });
      } finally {
        setLoadingBookings(false);
      }
    })();
  }, []);

  const submitReview = async () => {
    const booking = reviewModal.booking;
    if (!booking) return;
    if (!profileId) { toast({ title: t("profile.toast.login_required"), description: t("profile.toast.login_required_desc"), variant: "destructive" }); return; }
    const rating = Number(reviewModal.rating || 0);
    if (!(rating >= 1 && rating <= 5)) { toast({ title: t("profile.toast.invalid_rating"), description: t("profile.toast.invalid_rating_desc"), variant: "destructive" }); return; }
    if (!booking.company_id) { toast({ title: t("profile.toast.missing_company"), description: t("profile.toast.missing_company_desc"), variant: "destructive" }); return; }
    try {
      setReviewModal(prev => ({ ...prev, loading: true }));
      const { error } = await (supabase as any)
        .from('reviews')
        .insert({
          booking_id: booking.id,
          customer_id: profileId,
          company_id: booking.company_id,
          rating,
          comment: (reviewModal.comment || '').trim() ? (reviewModal.comment || '').trim() : null,
        });
      if (error) throw error;

      const newReview = { booking_id: booking.id, rating, comment: (reviewModal.comment || '').trim() || null, created_at: new Date().toISOString() };
      setReviewsByBookingId(prev => ({ ...prev, [booking.id]: newReview }));
      setBookings(prev => prev.map((b: any) => b.id === booking.id ? { ...b, review: newReview } : b));

      setReviewModal({ open: false, booking: null, rating: 5, comment: "", loading: false });
      toast({ title: t("profile.toast.review_sent"), description: t("profile.toast.review_sent_desc") });
    } catch (e: any) {
      const msg = String(e?.message || e);
      // Unique constraint or policy failures should be surfaced clearly
      if (/reviews_one_per_booking|duplicate key|unique/i.test(msg)) {
        toast({ title: t("profile.toast.review_exists"), description: t("profile.toast.review_exists_desc"), variant: "destructive" });
      } else if (/policy/i.test(msg) || /permission/i.test(msg)) {
        toast({ title: t("profile.toast.review_not_allowed"), description: t("profile.toast.review_not_allowed_desc"), variant: "destructive" });
      } else {
        toast({ title: t("profile.toast.error"), description: msg || t("profile.toast.review_failed"), variant: "destructive" });
      }
    } finally {
      setReviewModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handlePayNow = async (booking: any) => {
    try {
      const amount = Number(booking?.total_amount || 0);
      if (!(amount > 0)) {
        toast({ title: t("profile.toast.payment_unavailable"), description: t("profile.toast.payment_unavailable_desc"), variant: "destructive" });
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch('/api/ops.mjs?action=stripe_checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ booking_id: booking.id })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.url) {
        toast({ title: t("profile.toast.payment_error"), description: data?.error || t("profile.toast.payment_error_desc"), variant: "destructive" });
        return;
      }
      window.location.assign(data.url);
    } catch (e: any) {
      toast({ title: t("profile.toast.payment_error"), description: e?.message || t("profile.toast.payment_error_desc"), variant: "destructive" });
    }
  };

  const handleReschedule = async (bookingId: string) => {
    // Open modal and let the Save button perform the RPC with modal state
    setResModal({ id: bookingId, date: "", time: "", reason: "" } as any);
  };

  const submitReschedule = async () => {
    if (!resModal.id) return;
    try {
      if (!resModal.date || !resModal.time || !resModal.reason) { toast({ title: t("profile.toast.missing_fields"), description: t("profile.toast.missing_fields_reschedule"), variant: "destructive" }); return; }
      // Client-side 48h block guard
      try {
        const b = bookings.find((x: any) => x.id === resModal.id);
        if (b?.scheduled_date) {
          const timeStr = String(b.scheduled_time || '').includes('-') ? String(b.scheduled_time).split('-')[0].trim() : String(b.scheduled_time || '09:00');
          const target = new Date(`${b.scheduled_date}T${timeStr}:00`);
          const hours = (target.getTime() - Date.now()) / 3600000;
          if (hours <= 48) {
            toast({ title: t("profile.toast.blocked"), description: t("profile.toast.blocked_reschedule"), variant: "destructive" });
            return;
          }
        }
      } catch {}
      const toLocalYmd = (d: string) => { const dt = new Date(d); const y = dt.getFullYear(); const m = String(dt.getMonth()+1).padStart(2,'0'); const day = String(dt.getDate()).padStart(2,'0'); return `${y}-${m}-${day}`; };
      const { error } = await (supabase as any).rpc('customer_reschedule_booking', { p_booking_id: resModal.id, p_new_date: toLocalYmd(resModal.date as any), p_new_time: resModal.time, p_reason: resModal.reason });
      if (error) throw error;
      setBookings(prev => prev.map((b: any) => b.id === resModal.id ? { ...b, scheduled_date: resModal.date, scheduled_time: resModal.time } : b));
      
      // Calendar sync for customer reschedule
      try {
        console.log('Calling calendar sync for customer reschedule:', resModal.id);
        const response = await fetch("/api/calendar/sync-booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: resModal.id })
        });
        const result = await response.json();
        console.log('Calendar sync response (customer reschedule):', result);
      } catch (error) {
        console.error('Calendar sync error (customer reschedule):', error);
      }
      
      setResModal({ id: null, date: "", time: "", reason: "" });
      toast({ title: t("profile.toast.rescheduled") });
    } catch (e: any) {
      const msg = String(e?.message || e);
      if (msg.includes('cannot_reschedule_within_48h')) {
        toast({ title: t("profile.toast.blocked"), description: t("profile.toast.blocked_reschedule"), variant: "destructive" });
      } else {
        toast({ title: t("profile.toast.error"), description: msg || t("profile.toast.reschedule_failed"), variant: "destructive" });
      }
    }
  };

  const handleCancel = async (bookingId: string) => {
    // Load cost breakdown and vendor contacts, then open modal
    try {
      setCancelAck(false);
      setCancelInfo(null);
      const { data: row } = await (supabase as any)
        .from('bookings')
        .select('id, booking_number, status, total_amount, platform_fee_cents, customer_fee_cents, pricing_snapshot, scheduled_date, scheduled_time, companies(company_name, email, phone, commission_rate)')
        .eq('id', bookingId)
        .maybeSingle();
      if (row) {
        // Load latest succeeded payment separately to avoid nested-select/RLS issues
        let paidCents = 0;
        try {
          const payRes = await (supabase as any)
            .from('payments')
            .select('amount_cents,status,created_at')
            .eq('booking_id', bookingId)
            .order('created_at', { ascending: false })
            .limit(1);
          const p = Array.isArray(payRes?.data) ? payRes.data[0] : null;
          if (p && String(p.status) === 'succeeded') {
            paidCents = Number(p.amount_cents || 0);
          }
        } catch {}
        const totalCents = paidCents > 0 ? paidCents : Math.round(Number(row.total_amount || 0) * 100);

        // Commission rate from company (default 12%)
        const commissionRate = Number(row?.companies?.commission_rate ?? 0.12);

        // Use stored platform/customer fees when present, else compute sensible defaults
        const platformFeeCents = (row?.platform_fee_cents != null)
          ? Number(row.platform_fee_cents)
          : Math.max(Math.round(totalCents * commissionRate), 500); // €5 minimum

        const customerFeeComponentCents = (row?.customer_fee_cents != null)
          ? Number(row.customer_fee_cents)
          : Math.min(Math.round(totalCents * 0.02), 1000); // 2% capped at €10

        // Mirror the backend customer-cancel policy (api/ops.mjs:customer_cancel_booking):
        //  - >48h: full refund, Halomora absorbs the Stripe fee.
        //  - <=48h: retain the customer service fee + the (lost) Stripe fee from the refund.
        // The platform commission is NOT charged to the customer here (vendor is clawed back).
        let within48 = false;
        try {
          if (row?.scheduled_date) {
            const dt = new Date(`${row.scheduled_date}T${(row.scheduled_time || '00:00')}`);
            const hrs = (dt.getTime() - Date.now()) / 3600000;
            within48 = Number.isFinite(hrs) ? hrs <= 48 : true;
          }
        } catch { within48 = true; }
        // If the vendor covers the customer fee, the customer never paid it → no penalty.
        const vendorCoversFee = !!(row?.pricing_snapshot?.vendor_covers_customer_fee);
        const penaltyFeeCents = vendorCoversFee ? 0 : customerFeeComponentCents;
        // Stripe fee estimate (display only) — intentionally CONSERVATIVE so the
        // real refund (backend uses the ACTUAL charge.balance_transaction.fee) is
        // always >= this estimate, never less. We round the % UP and use a rate
        // that covers international / cross-border card pricing (~3.5% + €0.30),
        // since the EEA 1.5% rate underestimated real fees in testing. The backend
        // remains the source of truth; this only sets the customer's expectation.
        const STRIPE_FEE_EST_BPS = 350; // 3.50%
        const STRIPE_FEE_EST_FIXED_CENTS = 30; // €0.30
        const stripeFeeEstimateCents = within48
          ? (Math.ceil((totalCents * STRIPE_FEE_EST_BPS) / 10000) + STRIPE_FEE_EST_FIXED_CENTS)
          : 0;
        const retainedCents = within48 ? (penaltyFeeCents + stripeFeeEstimateCents) : 0;
        const estimatedRefundCents = Math.max(0, totalCents - retainedCents);

        setCancelInfo({
          booking_number: row.booking_number,
          total_paid: Number((totalCents / 100).toFixed(2)),
          platform_fee: Number((platformFeeCents / 100).toFixed(2)),
          vendor_fee: Number((penaltyFeeCents / 100).toFixed(2)),
          estimated_refund: Number((estimatedRefundCents / 100).toFixed(2)),
          within48,
          retained_total: Number((retainedCents / 100).toFixed(2)),
          stripe_fee: Number((stripeFeeEstimateCents / 100).toFixed(2)),
          vendor_email: row.companies?.email || null,
          vendor_phone: row.companies?.phone || null,
          company_name: row.companies?.company_name || null,
        });
      }
    } catch {}
    setCancelModal({ id: bookingId, reason: "" } as any);
  };

  const submitCancel = async () => {
    if (!cancelModal.id) return;
    try {
      if (!cancelModal.reason) { toast({ title: t("profile.toast.reason_required"), description: t("profile.toast.reason_required_desc"), variant: "destructive" }); return; }
      // Cancel + automatic refund handled server-side (48h policy, Stripe refund,
      // customer email + vendor notification). See api/ops.mjs:customer_cancel_booking.
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('not_authenticated');
      const resp = await fetch('/api/ops?action=customer_cancel_booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ booking_id: cancelModal.id, reason: cancelModal.reason, access_token: accessToken })
      });
      const result = await resp.json().catch(() => ({} as any));
      if (!resp.ok || !result?.ok) throw new Error(result?.error || 'cancel_failed');
      setBookings(prev => prev.map((b: any) => b.id === cancelModal.id ? { ...b, status: 'cancelled' } : b));
      if (typeof result.refund_amount_cents === 'number' && result.refund_amount_cents > 0) {
        toast({
          title: t("profile.toast.cancelled") || 'Prenotazione annullata',
          description: result.within48
            ? `Rimborso avviato (trattenuta la commissione di servizio per cancellazione entro 48h).`
            : `Rimborso totale avviato.`,
        });
      }

      // Calendar sync for customer cancel
      try {
        const response = await fetch("/api/calendar/sync-booking", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ booking_id: cancelModal.id })
        });
        await response.json();
      } catch {}

      setCancelModal({ id: null, reason: "" });
      setCancelAck(false);
      setCancelInfo(null);
      toast({
        title: t("profile.toast.cancelled_title"),
        description: 'La commissione della piattaforma sarà trattenuta, salvo accordo di riprogrammazione con il venditore.',
      });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.cancel_failed"), variant: "destructive" });
    }
  };

  const setConsent = async (checked: boolean) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const userId = session.user.id;
      const prof = await supabase.from('profiles').select('id').eq('user_id', userId).maybeSingle();
      if (prof.data?.id) {
        const { error } = await (supabase as any).from('profiles').update({ customer_consent: checked }).eq('id', prof.data.id);
        if (error) throw error;
        setCustomerConsent(checked);
        toast({ title: t("profile.toast.preferences_updated") });
      }
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.update_failed_generic"), variant: "destructive" });
    }
  };

  const setMarketing = async (checked: boolean) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const userId = session.user.id;
      const prof = await supabase.from('profiles').select('id').eq('user_id', userId).maybeSingle();
      if (prof.data?.id) {
        const payload: any = { marketing_consent: checked, marketing_consented_at: checked ? new Date().toISOString() : null };
        const { error } = await (supabase as any).from('profiles').update(payload).eq('id', prof.data.id);
        if (error) throw error;
        setMarketingConsent(checked);
        toast({ title: checked ? 'Marketing consent granted' : 'Marketing consent withdrawn' });
      }
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.update_failed_generic"), variant: "destructive" });
    }
  };

  const reConsentToLatest = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const prof = await supabase.from('profiles').select('id').eq('user_id', session.user.id).maybeSingle();
      if (prof.data?.id) {
        const payload: any = {
          terms_version: LEGAL_VERSIONS.terms,
          privacy_version: LEGAL_VERSIONS.privacy,
          consented_at: new Date().toISOString(),
          consent_source: 'reconsent'
        };
        const { error } = await (supabase as any).from('profiles').update(payload).eq('id', prof.data.id);
        if (error) throw error;
        setConsentInfo({ terms_version: LEGAL_VERSIONS.terms, privacy_version: LEGAL_VERSIONS.privacy, consented_at: payload.consented_at });
        toast({ title: t("profile.toast.consent_updated") });
      }
    } catch (e:any) {
      toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.update_failed_generic"), variant: "destructive" });
    }
  };

  useEffect(() => {
    // Open specified tab via URL hash (e.g., #addresses)
    const applyHash = () => {
      const hash = (window.location.hash || '').replace('#','');
      if (hash) {
        setActiveTab(normalizeProfileTab(hash));
      }
    };
    applyHash();
    const onHash = () => applyHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Support deep links: /profile?tab=bookings&review=<bookingId>
  useEffect(() => {
    const tab = (searchParams.get('tab') || '').trim();
    if (tab) {
      setActiveTab(normalizeProfileTab(tab));
    }
  }, [searchParams]);

  useEffect(() => {
    const reviewId = (searchParams.get('review') || '').trim();
    if (!reviewId) return;
    // Only open when we have bookings loaded
    if (!bookings || bookings.length === 0) return;
    const b = bookings.find((x: any) => x.id === reviewId);
    if (!b) return;
    if (String(b.status) !== 'completed') return;
    if ((b as any)?.review) return;
    setReviewModal({ open: true, booking: b, rating: 5, comment: "", loading: false });
  }, [bookings, searchParams]);

  useEffect(() => {
    // Load addresses on mount
    loadAddresses();
  }, []);

  return (
    <div key={lang} className="min-h-screen app-page">
      <Seo title={t("profile.seo.title")} noindex canonicalPath="/profile" />
      {StepUpDialog}
      {/* Header */}
      <div className="app-hero py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">{t('profile.title')}</h1>
              <p className="text-white/90">{t('profile.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <LanguageSwitcher compact />
            <Button 
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              {t('profile.back_to_dashboard')}
            </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Profile Overview Card */}
          <Card className="app-panel mb-8 border border-slate-200/70">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 min-w-0">
                <Avatar className="w-20 h-20 border border-slate-200 bg-slate-50">
                  <AvatarFallback className="text-lg bg-slate-100 text-slate-700">
                    {avatarInitials.first || avatarInitials.last
                      ? `${avatarInitials.first}${avatarInitials.last}`
                      : '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-slate-950 mb-1">
                    {t('profile.my_account')}
                  </h2>
                  <p className="mb-2 text-slate-500">{/* user email shown in avatar menu; optional duplicate here */}</p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <Badge variant="secondary" className="border-sky-200 bg-sky-50 text-sky-700">
                      <Star className="h-3 w-3 mr-1" />
                      {t('profile.upcoming')}: {overview.upcomingCount}
                    </Badge>
                    <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">
                      <Calendar className="h-3 w-3 mr-1" />
                      {t('profile.favourites')}: {overview.favouritesCount}
                    </Badge>
                  </div>
                </div>
                <div className="w-full sm:w-auto sm:text-right text-left min-w-0">
                  <div className="text-2xl font-bold text-slate-950 break-words">{formatCurrency(overview.totalSpent)}</div>
                  <div className="text-sm text-slate-600">{t('profile.total_spent')}</div>
                  <div className="text-sm text-slate-600 break-words">{overview.totalOrders} {t('profile.orders')}</div>
                </div>
              </div>
            </CardContent>
          </Card>

      {/* Reschedule Modal */}
      {resModal.id && (
        <div className="app-modal-overlay">
          <div className="app-modal-panel w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('profile.reschedule_booking')}</h3>
              <button onClick={() => setResModal({ id: null, date: "", time: "", reason: "" })} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <Label>{t('profile.new_date')}</Label>
                <Input type="date" value={resModal.date} onChange={(e) => setResModal(prev => ({ ...prev, date: e.target.value }))} />
              </div>
              <div>
                <Label>{t('profile.new_time')}</Label>
                <Input type="time" value={resModal.time} onChange={(e) => setResModal(prev => ({ ...prev, time: e.target.value }))} />
              </div>
              <div>
                <Label>{t('profile.reason')}</Label>
                <Textarea value={resModal.reason} onChange={(e) => setResModal(prev => ({ ...prev, reason: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-gray-300" onClick={() => setResModal({ id: null, date: "", time: "", reason: "" })}>{t('profile.cancel')}</Button>
              <Button onClick={submitReschedule}>{t('profile.save')}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal.open && reviewModal.booking && (
        <div className="app-modal-overlay">
          <div className="app-modal-panel w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('profile.review.title')}</h3>
              <button
                onClick={() => setReviewModal({ open: false, booking: null, rating: 5, comment: "", loading: false })}
                className="text-gray-500 hover:text-gray-700"
                aria-label={t('profile.close')}
              >
                ✕
              </button>
            </div>
            <div className="text-sm text-gray-700">
              {reviewModal.booking?.services?.name || t('profile.fallback.service')} • {reviewModal.booking?.companies?.company_name || t('profile.fallback.vendor')}
            </div>
            <div className="space-y-2">
              <Label>{t('profile.review.rating')}</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReviewModal(prev => ({ ...prev, rating: i }))}
                    className="p-1"
                    aria-label={tx('profile.rate_star', { n: i })}
                  >
                    <Star className={`h-6 w-6 ${i <= (reviewModal.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">{reviewModal.rating}/5</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('profile.review.comment_optional')}</Label>
              <Textarea
                value={reviewModal.comment}
                onChange={(e) => setReviewModal(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={t('profile.review.placeholder')}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                className="border-gray-300"
                disabled={reviewModal.loading}
                onClick={() => setReviewModal({ open: false, booking: null, rating: 5, comment: "", loading: false })}
              >
                {t('profile.cancel')}
              </Button>
              <Button disabled={reviewModal.loading} onClick={submitReview}>
                {reviewModal.loading ? t('profile.review.submitting') : t('profile.review.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal.id && (
        <div className="app-modal-overlay">
          <div className="app-modal-panel w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('profile.cancel_booking')}</h3>
              <button onClick={() => setCancelModal({ id: null, reason: "" })} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                {t('profile.cancel_policy_notice')}
              </div>
              {/* Dynamic 48h policy banner */}
              <div className={`rounded-lg border p-3 text-sm ${cancelInfo?.within48 ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-300 bg-emerald-50 text-emerald-900'}`}>
                {cancelInfo?.within48
                  ? `Cancellazione entro 48h dall'appuntamento: verrà trattenuta la commissione di servizio${(cancelInfo?.stripe_fee || 0) > 0 ? ' e i costi di transazione' : ''} (${formatCurrency(cancelInfo?.retained_total || 0)}). Il resto ti verrà rimborsato.`
                  : `Cancellazione con oltre 48h di anticipo: riceverai il rimborso totale di quanto pagato.`}
              </div>
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="text-sm font-medium text-gray-900 mb-2">{t('profile.cost_breakdown')}</div>
                <div className="text-sm text-gray-700 space-y-1">
                  <div className="flex justify-between">
                    <span>{t('profile.total_paid')}</span>
                    <span>{formatCurrency(cancelInfo?.total_paid || 0)}</span>
                  </div>
                  {cancelInfo?.within48 && (cancelInfo?.vendor_fee || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>{t('profile.vendor_cancel_fee')}</span>
                      <span>− {formatCurrency(cancelInfo?.vendor_fee || 0)}</span>
                    </div>
                  )}
                  {cancelInfo?.within48 && (cancelInfo?.stripe_fee || 0) > 0 && (
                    <div className="flex justify-between">
                      <span>Costi di transazione</span>
                      <span>− {formatCurrency(cancelInfo?.stripe_fee || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span>{t('profile.estimated_refund')}</span>
                    <span>{formatCurrency(cancelInfo?.estimated_refund || 0)}</span>
                  </div>
                </div>
                <div className="text-[11px] text-gray-500 mt-2">Importi stimati; il rimborso effettivo è calcolato da Stripe.</div>
              </div>
              <div>
                <Label>{t('profile.reason')}</Label>
                <Textarea value={cancelModal.reason} onChange={(e) => setCancelModal(prev => ({ ...prev, reason: e.target.value }))} />
              </div>
              <div className="flex items-start gap-2">
                <input id="ack48" type="checkbox" className="mt-1" checked={cancelAck} onChange={(e) => setCancelAck(e.target.checked)} />
                <label htmlFor="ack48" className="text-xs text-gray-700">
                  {t('profile.ack_fee_retention')}
                </label>
              </div>
              <div className="pt-2 space-y-2">
                {(cancelInfo?.vendor_email || cancelInfo?.vendor_phone) && (
                  <div className="flex flex-wrap gap-2">
                    {cancelInfo?.vendor_email && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 whitespace-nowrap"
                        onClick={() => window.open(`mailto:${cancelInfo?.vendor_email}?subject=${encodeURIComponent(tx('profile.mailto.reschedule_subject', { number: cancelInfo?.booking_number || '' }))}`, '_blank')}
                      >
                        {t('profile.contact_vendor')}
                      </Button>
                    )}
                    {cancelInfo?.vendor_phone && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 whitespace-nowrap"
                        onClick={() => window.open(`tel:${cancelInfo?.vendor_phone}`, '_self')}
                      >
                        {t('profile.call_vendor')}
                      </Button>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    className="border-gray-300 w-full sm:w-auto h-10"
                    onClick={() => setCancelModal({ id: null, reason: "" })}
                  >
                    {t('profile.back')}
                  </Button>
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto h-10"
                    disabled={!cancelAck}
                    onClick={submitCancel}
                  >
                    {t('profile.confirm_cancel')}
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-gray-500">
                {t('profile.vendor_can_reschedule')}
              </div>
            </div>
          </div>
        </div>
      )}
          {/* Main Content */}
          <div className="app-panel max-w-full">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b border-gray-200 px-4">
                <TabsList className="flex w-full gap-2 justify-start bg-transparent rounded-none p-0 py-2 pl-6 pr-6 overflow-x-auto overflow-y-hidden min-h-[2.75rem] min-w-0 [scrollbar-width:thin]">
                  <TabsTrigger 
                    value="bookings" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <Calendar className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.bookings')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="personal" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <User className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.personal')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="addresses" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <MapPin className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.addresses')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notifications" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <Bell className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.notifications')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="loyalty" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <Gift className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.loyalty')}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="security" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <Shield className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.security')}
                  </TabsTrigger>
                  {QUOTES_ENABLED && (
                    <TabsTrigger
                      value="quotes"
                      className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                    >
                      <MessageSquare className="h-4 w-4 mr-2 inline-block" />
                      {t('profile.tab.quotes')}
                    </TabsTrigger>
                  )}
                  <TabsTrigger 
                    value="billing" 
                    className="whitespace-nowrap shrink-0 rounded-xl px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm"
                  >
                    <FileText className="h-4 w-4 mr-2 inline-block" />
                    {t('profile.tab.billing')}
                  </TabsTrigger>
                </TabsList>
                {/* Quotes (gated by feature flag) */}
                {QUOTES_ENABLED && (
                <TabsContent value="quotes" className="space-y-6">
                  <Card className="border-gray-200 shadow-card">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('profile.my_quote_requests')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {quotes.length === 0 ? (
                        <div className="text-sm text-gray-600">{t('profile.no_quotes_yet')}</div>
                      ) : (
                        <div className="space-y-3">
                          {quotes.map((q: any) => (
                            <div key={q.id} className="p-3 border rounded-lg flex items-center justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="bg-gray-100 text-gray-800">{String(q.status)}</Badge>
                                  {q.quote_price ? <span className="font-medium">{formatCurrency(Number(q.quote_price))}</span> : <span className="text-sm text-gray-600">{t('profile.awaiting_quote')}</span>}
                                </div>
                                {q.quote_message && <div className="text-sm text-gray-700">{q.quote_message}</div>}
                                <div className="text-xs text-gray-600">{t('profile.requested_on')} {new Date(q.created_at).toLocaleDateString()}</div>
                              </div>
                              <div className="flex gap-2">
                                {q.status === 'quoted' ? (
                                  <>
                                    <Button size="sm" onClick={() => { setAcceptForQuoteId(q.id); setAcceptDate(""); setAcceptTime(""); }}>{t('profile.accept')}</Button>
                                    <Button size="sm" variant="outline" onClick={async () => {
                                      await (supabase as any).from('quote_requests').update({ status: 'rejected' }).eq('id', q.id);
                                      setQuotes(prev => prev.map(r => r.id === q.id ? { ...r, status: 'rejected' } : r));
                                    }}>{t('profile.reject')}</Button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  {acceptForQuoteId && (
                    <div className="app-modal-overlay">
                      <div className="app-modal-panel w-full max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">{t('profile.confirm_quote')}</h3>
                          <button onClick={() => setAcceptForQuoteId(null)} className="text-gray-500 hover:text-gray-700">✕</button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label>{t('profile.date')}</Label>
                            <Input type="date" value={acceptDate} onChange={(e) => setAcceptDate(e.target.value)} />
                          </div>
                          <div>
                            <Label>{t('profile.time')}</Label>
                            <Input type="time" value={acceptTime} onChange={(e) => setAcceptTime(e.target.value)} />
                          </div>
                          <p className="text-xs text-gray-500">{t('profile.quote_confirm_hint')}</p>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button variant="outline" className="border-gray-300" onClick={() => setAcceptForQuoteId(null)}>{t('profile.cancel')}</Button>
                          <Button onClick={async () => {
                            try {
                              if (!acceptDate || !acceptTime) { toast({ title: t("profile.toast.missing_fields"), description: t("profile.toast.pick_date_time"), variant: "destructive" }); return; }
                              const q = quotes.find((x: any) => x.id === acceptForQuoteId);
                              if (!q) return;
                              const { data: { session } } = await supabase.auth.getSession();
                              if (!session) { navigate('/auth'); return; }
                              const prof = await supabase.from('profiles').select('id').eq('user_id', session.user.id).maybeSingle();
                              const customerId = prof.data?.id;
                              const payload: any = {
                                customer_id: customerId,
                                customer_name: mockUser.firstName + ' ' + mockUser.lastName,
                                customer_email: session.user.email,
                                customer_phone: '',
                                scheduled_date: acceptDate,
                                scheduled_time: acceptTime,
                                total_amount: q.quote_price || 0,
                                status: 'pending',
                                quote_request_id: q.id,
                                idempotency_key: `${q.id}-${acceptDate}-${acceptTime}-${session.user.id}`,
                              };
                              // Create booking via by_ids
                              const rpc = await (supabase as any).rpc('create_public_booking_by_ids', {
                                p_company_id: q.company_id,
                                p_service_id: q.service_id,
                                p_payload: payload,
                              });
                              if (rpc.error) throw rpc.error;
                              // Mark quote accepted
                              await (supabase as any).from('quote_requests').update({ status: 'accepted' }).eq('id', q.id);
                              setQuotes(prev => prev.map((r: any) => r.id === q.id ? { ...r, status: 'accepted' } : r));
                              // Notify vendor
                              try {
                                const start = new Date(`${acceptDate}T${acceptTime || '09:00'}`);
                                const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
                                const dt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                                const ics = [
                                  'BEGIN:VCALENDAR',
                                  'VERSION:2.0',
                                  'PRODID:-//Halomora//Bookings//EN',
                                  'BEGIN:VEVENT',
                                  `UID:${q.id || `${Date.now()}@halomora`}`,
                                  `DTSTAMP:${dt(new Date())}`,
                                  `DTSTART:${dt(start)}`,
                                  `DTEND:${dt(end)}`,
                                  `SUMMARY:${(q as any)?.service_name || 'Service'} - ${(q as any)?.company_name || ''}`,
                                  `DESCRIPTION:Quote accepted for request ${q.id}`,
                                  'END:VEVENT',
                                  'END:VCALENDAR'
                                ].join('\r\n');
                                await fetch('/api/notifications/enqueue', {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ template: 'quote_accepted', to_role: 'vendor', payload: { quote_id: q.id, quoted_total: formatCurrency(Number(q.quote_price || 0)), ics_filename: `quote-${q.id}.ics`, ics_content_base64: typeof btoa !== 'undefined' ? btoa(ics) : Buffer.from(ics).toString('base64') } })
                                });
                              } catch {}
                              toast({ title: t("profile.toast.booking_created"), description: t("profile.toast.booking_created_desc") });
                              setAcceptForQuoteId(null);
                            } catch (e: any) {
                              toast({ title: t("profile.toast.error"), description: e.message || t("profile.toast.quote_accept_failed"), variant: "destructive" });
                            }
                          }}>{t('profile.confirm_booking')}</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>
                )}
              </div>

              <div className="p-6 max-w-full overflow-x-hidden">
                {/* Bookings */}
                <TabsContent value="bookings" className="space-y-6">
                  {/* Filters */}
                  <Card className="border-gray-200 shadow-card overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900 break-words">{t('profile.filter_bookings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="max-w-full overflow-x-hidden">
                      <BookingFilters
                        filters={bookingFilters}
                        onFiltersChange={setBookingFilters}
                        options={bookingFilterOptions}
                        showCompanyFilter={true}
                        showServiceFilter={true}
                        showStatusFilter={true}
                        showDateFilter={true}
                        showSorting={true}
                      />
                    </CardContent>
                  </Card>

                  {/* Bookings List */}
                  <Card className="border-gray-200 shadow-card overflow-hidden">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900 break-words">
                        {tx('profile.my_bookings', { count: filteredBookings.length })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="max-w-full overflow-x-hidden">
                      <div className="flex justify-end mb-3">
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              const { data: { session } } = await supabase.auth.getSession();
                              if (!session) { navigate('/auth'); return; }
                              const month = new Date().toISOString().slice(0,7);
                              const resp = await fetch(`/api/stripe?action=customer_statement_csv&month=${encodeURIComponent(month)}`, {
                                method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}` }
                              });
                              const blob = await resp.blob();
                              if (!resp.ok) throw new Error('Download failed');
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = `customer_statement_${month}.csv`; a.click(); URL.revokeObjectURL(url);
                            } catch (e:any) {
                              toast({ title: t('profile.toast.error'), description: e.message || t('profile.toast.error'), variant: 'destructive' });
                            }
                          }}
                        >
                          {t('profile.download_statement')}
                        </Button>
                      </div>
                      {loadingBookings ? (
                        <div className="text-sm text-gray-600">{t('profile.loading')}</div>
                      ) : filteredBookings.length === 0 ? (
                        <div className="text-sm text-gray-600">
                          {bookings.length === 0 ? t('profile.no_bookings_yet') : t('profile.no_bookings_match')}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredBookings.map((b) => (
                            <div key={b.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border rounded-lg">
                              <div className="space-y-1 min-w-0 w-full">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="bg-gray-100 text-gray-800">{String(b.status).replace('_',' ')}</Badge>
                                  <span className="font-medium">{b.booking_number}</span>
                                </div>
                                <div className="text-sm text-gray-700">{b?.services?.name || t('profile.fallback.service')} • {b?.companies?.company_name || t('profile.fallback.vendor')}</div>
                                {(b?.companies?.email || b?.companies?.phone) && (
                                  <div className="text-xs text-gray-600">
                                    {[b?.companies?.email, b?.companies?.phone].filter(Boolean).join(' • ')}
                                  </div>
                                )}
                                <div className="text-xs text-gray-600">{b.scheduled_date} {b.scheduled_time}</div>
                              </div>
                              <div className="flex flex-wrap gap-2 items-center justify-end w-full sm:w-auto">
                                <Button size="sm" variant="secondary" onClick={() => navigate(`/booking/${b.id}`)}>
                                  {t('profile.details')}
                                </Button>
                                {(b as any)?.review && (
                                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-600 mr-2">
                                    {[1,2,3,4,5].map((i) => (
                                      <Star key={i} className={`h-3 w-3 ${(i <= Number((b as any)?.review?.rating || 0)) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                                    ))}
                                  </div>
                                )}
                                {String(b.status) === 'completed' && !(b as any)?.review && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setReviewModal({ open: true, booking: b, rating: 5, comment: "", loading: false })}
                                  >
                                    {t('profile.leave_review')}
                                  </Button>
                                )}
                                {String(b.status) === 'pending' &&
                                  Number(b.total_amount || 0) > 0 &&
                                  !b.series_id &&
                                  String((b as any).payment_method || '').toLowerCase() === 'card' &&
                                  !(b as any).batch_id &&
                                  !(b as any).receipt_url &&
                                  !(b as any).payment_intent_id &&
                                  !(b as any).charge_id && (
                                  <Button size="sm" onClick={() => handlePayNow(b)}>{t('profile.pay_now')}</Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => setResModal({ id: b.id, date: "", time: "", reason: "" })}>{t('profile.reschedule')}</Button>
                                <Button size="sm" variant="destructive" onClick={() => handleCancel(b.id)}>{t('profile.cancel_action')}</Button>
                                {b.receipt_url && (
                                  <Button size="sm" variant="outline" onClick={() => window.open(b.receipt_url, '_blank')}>{t('profile.download_receipt')}</Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Billing / Fatturazione */}
                <TabsContent value="billing" className="space-y-6">
                  <ProfileBillingSection />
                </TabsContent>

                {/* Personal Information */}
                <TabsContent value="personal" className="space-y-6">
                  <Card className="border-gray-200 shadow-card">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('profile.personal_info')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Form {...profileForm}>
                        <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                              control={profileForm.control}
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.first_name')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="lastName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.last_name')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="nickname"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.nickname_optional')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.email_address')}</FormLabel>
                                  <FormControl>
                                    <Input type="email" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.phone_number')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={profileForm.control}
                              name="website"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-700">{t('profile.website_optional')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={profileForm.control}
                            name="bio"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-700">{t('profile.bio_optional')}</FormLabel>
                                <FormControl>
                                  <Textarea 
                                    placeholder={t('profile.bio_placeholder')}
                                    className="resize-none"
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="flex justify-end">
                            <Button type="submit" className="bg-primary hover:bg-primary/90">
                              {t('profile.save_changes')}
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Addresses */}
                <TabsContent value="addresses" className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">{t('profile.addresses_title')}</h2>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-primary hover:bg-primary/90 flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          {t('profile.add_new_address')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('profile.add_new_address')}</DialogTitle>
                          <DialogDescription>
                            {t('profile.add_address_dialog_desc')}
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...addressForm}>
                          <form onSubmit={addressForm.handleSubmit(onSubmitAddress)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={addressForm.control}
                                name="label"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.field.label')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="addressLine1"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.address_line1')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="addressLine2"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.address_line2_optional')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="city"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.city')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="state"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.state')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="postalCode"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.postal_code')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={addressForm.control}
                                name="country"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.country')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <Button type="submit" className="bg-primary hover:bg-primary/90">
                              {t('profile.add_address_btn')}
                            </Button>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="space-y-4">
                    {addresses.map((address) => (
                      <Card key={address.id} className="bg-gray-50 border border-gray-200 shadow-sm">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-gray-900">{address.label}</h3>
                                {address.isDefault && (
                                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                                    {t('profile.default')}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-gray-700">{address.addressLine1}</p>
                              {address.addressLine2 && <p className="text-gray-700">{address.addressLine2}</p>}
                              <p className="text-gray-700">{address.city}, {address.state} {address.postalCode}</p>
                              <p className="text-gray-700">{address.country}</p>
                            </div>
                            <div className="flex items-center gap-2">
                            {!address.isDefault && (
                              <Button variant="outline" size="sm" onClick={() => setDefaultAddress(address.id)}>
                                {t('profile.set_default')}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingAddress(address);
                                editAddressForm.reset({
                                  label: address.label,
                                  addressLine1: address.addressLine1,
                                  addressLine2: address.addressLine2 || "",
                                  city: address.city,
                                  state: address.state || "",
                                  postalCode: address.postalCode,
                                  country: address.country,
                                  isDefault: !!address.isDefault,
                                });
                              }}
                            >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => removeAddress(address.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                {editingAddress && (
                  <div className="app-modal-overlay">
                    <div className="app-modal-panel w-full max-w-md p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">{t('profile.edit_address')}</h3>
                        <button onClick={() => setEditingAddress(null)} className="text-gray-500 hover:text-gray-700">✕</button>
                      </div>
                      <Form {...editAddressForm}>
                        <form
                          onSubmit={editAddressForm.handleSubmit((data) => updateAddress(editingAddress.id, data))}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                              control={editAddressForm.control}
                              name="label"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.field.label')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="addressLine1"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.address_line1')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="addressLine2"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.address_line2_optional')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="city"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.city')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="state"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.state')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="postalCode"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.postal_code')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={editAddressForm.control}
                              name="country"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('profile.country')}</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-2">
                            <Button type="button" variant="outline" className="border-gray-300" onClick={() => setEditingAddress(null)}>{t('profile.cancel')}</Button>
                            <Button type="submit">{t('profile.save')}</Button>
                          </div>
                        </form>
                      </Form>
                    </div>
                  </div>
                )}
                </TabsContent>

                {/* Payment Methods */}
                <TabsContent value="payments" className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">{t('profile.payment_methods_title')}</h2>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-primary hover:bg-primary/90 flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          {t('profile.add_payment_method')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('profile.add_payment_dialog_title')}</DialogTitle>
                          <DialogDescription>
                            {t('profile.add_payment_dialog_desc')}
                          </DialogDescription>
                        </DialogHeader>
                        <Form {...paymentForm}>
                          <form onSubmit={paymentForm.handleSubmit(onSubmitPayment)} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <FormField
                                control={paymentForm.control}
                                name="cardNumber"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.card_number')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} type={showPassword ? "text" : "password"} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={paymentForm.control}
                                name="cardholderName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.cardholder_name')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={paymentForm.control}
                                name="expiryMonth"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.expiry_month')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} type="number" min="1" max="12" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={paymentForm.control}
                                name="expiryYear"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.expiry_year')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} type="number" min="2024" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={paymentForm.control}
                                name="cvv"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>{t('profile.cvv')}</FormLabel>
                                    <FormControl>
                                      <Input {...field} type={showPassword ? "text" : "password"} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <Button type="submit" className="bg-primary hover:bg-primary/90">
                              {t('profile.add_payment_btn')}
                            </Button>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="space-y-4">
                    {paymentMethods.map((payment) => (
                      <Card key={payment.id} className="bg-gray-50 border border-gray-200 shadow-sm">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                                <CreditCard className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{payment.type} **** {payment.last4}</span>
                                  {payment.isDefault && (
                                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                                      Default
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700">{tx('profile.expires', { date: payment.expires })}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => removePaymentMethod(payment.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Notifications */}
                <TabsContent value="notifications" className="space-y-6">
                
                  <Card className="border-gray-200 shadow-card">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('profile.notifications_privacy_title')}</CardTitle>
                    </CardHeader>
                  <CardContent className="space-y-6">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                        {t('profile.notifications_intro')}
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-900">{t('profile.service_comms_title')}</h3>
                          <p className="text-sm text-gray-600">{t('profile.service_comms_desc')}</p>
                        </div>
                        <Switch checked={customerConsent} onCheckedChange={setConsent} />
                      </div>
                      <div className="rounded-xl border border-primary/15 bg-gradient-to-r from-primary/5 to-accent-green-500/5 p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{t('profile.marketing_consent')}</h3>
                            <p className="mt-1 text-sm text-gray-600">
                              {t('profile.marketing_desc')}
                            </p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              <div className="rounded-lg border border-white/70 bg-white/80 p-3 text-sm text-slate-700">
                                {t('profile.marketing.benefit1')}
                              </div>
                              <div className="rounded-lg border border-white/70 bg-white/80 p-3 text-sm text-slate-700">
                                {t('profile.marketing.benefit2')}
                              </div>
                              <div className="rounded-lg border border-white/70 bg-white/80 p-3 text-sm text-slate-700">
                                {t('profile.marketing.benefit3')}
                              </div>
                              <div className="rounded-lg border border-white/70 bg-white/80 p-3 text-sm text-slate-700">
                                {t('profile.marketing.benefit4')}
                              </div>
                            </div>
                            <p className="mt-3 text-xs text-slate-500">
                              {t('profile.marketing_opt_out_note')}
                            </p>
                          </div>
                          <Switch checked={marketingConsent} onCheckedChange={setMarketing} />
                        </div>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <h3 className="font-medium text-gray-900">{t('profile.consent_history')}</h3>
                        <p className="text-sm text-gray-600">{t('profile.terms_accepted_label')} <span className="font-medium">{consentInfo.terms_version || t('profile.not_available')}</span></p>
                        <p className="text-sm text-gray-600">{t('profile.privacy_accepted_label')} <span className="font-medium">{consentInfo.privacy_version || t('profile.not_available')}</span></p>
                        <p className="text-sm text-gray-600">{t('profile.consent_date_label')} <span className="font-medium">{consentInfo.consented_at ? new Date(consentInfo.consented_at).toLocaleString() : t('profile.not_available')}</span></p>
                        <div className="mt-2">
                          <Button variant="outline" onClick={reConsentToLatest}>{t('profile.update_to_latest')}</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Loyalty & Rewards */}
                <TabsContent value="loyalty" className="space-y-6">
                  <Card className="border-gray-200 shadow-card bg-gradient-to-br from-primary/5 to-accent-green-500/5">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-gray-900">{t('profile.loyalty_card_title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="rounded-xl border border-primary/10 bg-white/85 p-5">
                        <div className="text-lg font-semibold text-slate-950">{t('profile.loyalty_coming_soon')}</div>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {t('profile.loyalty_intro1')}
                        </p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {t('profile.loyalty_intro2')}
                        </p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-semibold text-slate-900">{t('profile.loyalty.discount_codes')}</div>
                          <p className="mt-2 text-sm text-slate-600">{t('profile.loyalty.coupon_desc')}</p>
                          <Badge variant="outline" className="mt-3">{t('profile.loyalty.no_codes')}</Badge>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-semibold text-slate-900">{t('profile.loyalty.promo_credits')}</div>
                          <p className="mt-2 text-sm text-slate-600">{t('profile.loyalty.promo_credits_desc')}</p>
                          <Badge variant="outline" className="mt-3">{t('profile.loyalty.coming_soon_badge')}</Badge>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="text-sm font-semibold text-slate-900">{t('profile.loyalty.benefits')}</div>
                          <p className="mt-2 text-sm text-slate-600">{t('profile.loyalty.benefits_desc')}</p>
                          <Badge variant="outline" className="mt-3">{t('profile.loyalty.area_ready')}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Security */}
                <TabsContent value="security" className="space-y-6">
                  <Card className="app-panel border border-slate-200/70">
                    <CardHeader>
                      <CardTitle className="text-xl font-semibold text-slate-950">{t('profile.security_title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="rounded-[1.25rem] border border-slate-200/80 bg-white/90 p-4 shadow-soft flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-slate-950">{t('profile.change_password')}</h3>
                          <p className="text-sm text-slate-600">{t('profile.security.change_password_desc')}</p>
                        </div>
                        <Button variant="outline" onClick={async () => {
                          try {
                            const { data: { session } } = await supabase.auth.getSession();
                            if (!session?.user?.email) { toast({ title: t("profile.toast.sign_in_required"), variant: "destructive" }); return; }
                            // Supabase: send password reset email
                            const { error } = await supabase.auth.resetPasswordForEmail(session.user.email, { redirectTo: window.location.origin + '/auth/reset' });
                            if (error) throw error;
                            toast({ title: t('profile.toast.email_sent'), description: t('profile.toast.email_sent_desc') });
                          } catch (e: any) {
                            const msg = String(e?.message || "").toLowerCase();
                            if (msg.includes("rate limit")) {
                              toast({ title: t("profile.toast.rate_limit"), description: t("profile.toast.rate_limit_reset"), variant: "destructive" });
                            } else {
                            toast({ title: t('profile.toast.error'), description: e.message || t('profile.toast.update_failed_generic'), variant: 'destructive' });
                            }
                          }
                        }}>
                          <Lock className="h-4 w-4 mr-2" />
                          {t('profile.security.change_password_btn')}
                        </Button>
                      </div>

                      <div className="rounded-[1.25rem] border border-slate-200/80 bg-white/90 p-4 shadow-soft flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-slate-950">{t('profile.two_factor')}</h3>
                          <p className="text-sm text-slate-600">{t('profile.mfa.dialog_desc')}</p>
                        </div>
                        <Dialog onOpenChange={(open) => { if (open) loadMfa(); }}>
                          <DialogTrigger asChild>
                            <Button variant="outline">
                          <Shield className="h-4 w-4 mr-2" />
                              {mfaFactors.length > 0 ? t("profile.security.manage_2fa") : t("profile.security.enable_2fa")}
                        </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>{t("profile.mfa.dialog_title")}</DialogTitle>
                              <DialogDescription>
                                {t("profile.mfa.dialog_desc")}
                              </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4">
                              {mfaError && (
                                <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2">
                                  {mfaError}
                                </div>
                              )}

                              <div className="text-sm text-gray-700">
                                {t("profile.mfa.status_label")} <span className="font-medium">{mfaFactors.length > 0 ? t("profile.mfa.status_enabled") : t("profile.mfa.status_disabled")}</span>
                              </div>

                              {mfaFactors.length === 0 ? (
                                <>
                                  <Button disabled={mfaLoading} onClick={startEnable2FA}>
                                    {mfaLoading ? t("profile.mfa.starting") : t("profile.mfa.start_setup")}
                                  </Button>

                                  {mfaQrSvg && (
                                    <div className="space-y-2">
                                      <div className="text-sm text-gray-700">{t("profile.mfa.scan_qr")}</div>
                                      <div className="border rounded p-3 bg-white" dangerouslySetInnerHTML={{ __html: mfaQrSvg }} />
                                      {mfaUri && (
                                        <div className="text-xs text-gray-500 break-all">
                                          {t("profile.mfa.uri_hint")} {mfaUri}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {mfaChallenge?.id && (
                                    <div className="space-y-2">
                                      <div className="text-sm text-gray-700">{t("profile.mfa.enter_code")}</div>
                                      <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}>
                                        <InputOTPGroup>
                                          <InputOTPSlot index={0} />
                                          <InputOTPSlot index={1} />
                                          <InputOTPSlot index={2} />
                                          <InputOTPSlot index={3} />
                                          <InputOTPSlot index={4} />
                                          <InputOTPSlot index={5} />
                                        </InputOTPGroup>
                                      </InputOTP>
                                      <Button disabled={mfaLoading || (mfaCode || "").replace(/\\s/g,'').length !== 6} onClick={verifyEnable2FA}>
                                        {mfaLoading ? t("profile.mfa.verifying") : t("profile.mfa.verify_enable")}
                                      </Button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="text-sm text-gray-700">
                                    {t("profile.mfa.enabled_message")}
                                  </div>
                                  <Button variant="destructive" disabled={mfaLoading} onClick={disable2FA}>
                                    {mfaLoading ? t("profile.mfa.disabling") : t("profile.mfa.disable")}
                                  </Button>
                                </>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900">{t('profile.security.session_title')}</h3>
                          <p className="text-sm text-gray-600">{t('profile.security.session_desc')}</p>
                        </div>
                        <Button variant="outline" className="w-full sm:w-auto" onClick={async () => {
                          try {
                            const { data: { session } } = await supabase.auth.getSession();
                            if (!session) { navigate('/auth'); return; }
                            const resp = await supabase.auth.getUser();
                            const current = resp.data.user;
                            // Placeholder session view: show user id/email and created_at
                            toast({
                              title: t('profile.security.active_session'),
                              description: tx('profile.security.active_session_desc', {
                                email: current?.email || '',
                                since: new Date(current?.created_at || '').toLocaleString(),
                              }),
                            });
                          } catch {}
                        }}>
                          <Clock className="h-4 w-4 mr-2" />
                          {t('profile.security.manage_sessions')}
                        </Button>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900">{t('profile.export_data')}</h3>
                          <p className="text-sm text-gray-600">{t('profile.export_data_desc')}</p>
                        </div>
                        <Button variant="outline" className="w-full sm:w-auto" onClick={handleExportData}>
                          {t('profile.export_json')}
                        </Button>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg">
                        <div className="min-w-0">
                          <h3 className="font-medium text-gray-900">{t('profile.delete_account')}</h3>
                          <p className="text-sm text-gray-600">{t('profile.toast.account_deletion_desc')}</p>
                        </div>
                        <Button variant="destructive" className="w-full sm:w-auto" onClick={handleRequestDeletion}>
                          {t('profile.delete_account_btn')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;